import { useCallback, useState, type ReactNode } from 'react';
import { ExpandedIcon } from '~/components/common/icons/ExpandedIcon';
import { cn } from '~/utils/common';

export type ControlledCollapseProps = {
  open: boolean;
  onToggle: () => void;
  className?: string;
  title: ReactNode;
  titleClassName?: string;
  children: ReactNode;
  contentClassName?: string;
  withArrow?: boolean;
};

export function ControlledCollapse({
  open,
  onToggle,
  className,
  title,
  titleClassName,
  children,
  contentClassName,
  withArrow = true,
}: ControlledCollapseProps) {
  return (
    <div className={cn('', className)}>
      <button
        type="button"
        className="w-full flex justify-between items-center"
        onClick={onToggle}
      >
        <div className={titleClassName}>{title}</div>
        {withArrow && <ExpandedIcon expanded={open} />}
      </button>

      {open && <div className={contentClassName}>{children}</div>}
    </div>
  );
}

export function CollapsibleCard({
  title,
  defaultOpen,
  ...collapseProps
}: Omit<ControlledCollapseProps, 'open' | 'onToggle'> & {
  defaultOpen?: boolean;
}) {
  const controls = useCollapseControl(defaultOpen);

  return (
    <ControlledCollapsibleCard title={title} {...collapseProps} {...controls} />
  );
}

export function ControlledCollapsibleCard({
  title,
  ...collapseProps
}: ControlledCollapseProps) {
  return (
    <div className="card card-border shadow-sm">
      <div className="card-body py-2">
        <ControlledCollapse
          title={
            <h3 className="card-title flex justify-between items-center gap-4">
              {title}
            </h3>
          }
          {...collapseProps}
        />
      </div>
    </div>
  );
}

export function useCollapseControl(initialOpen = false) {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);

  return {
    open: isOpen,
    setIsOpen,
    onToggle: toggleOpen,
  };
}
